import React from 'react'
import PropTypes from 'prop-types'
import { midas } from '@resurs/midas'
import { Checkbox, Switch } from '@resurs/midas-react'
import { getText } from '../../../../internal/TextLabels'
import { format } from '../../../../models/format'
import { FormSwitch } from './FormSwitch'
import FormTextInput from './FormTextInput'
import { FormSelect } from './FormSelect'
import CurrencyInput from '../../../../internal/CurrencyInput'

const getValuesForSelect = valueList =>
  valueList
    .filter(
      value =>
        !!value.value && !['UNKNOWN', 'noanswer', 'na'].includes(value.value),
    )
    .map(({ value, label }, key) => ({
      key: `${value} ${key}`,
      value,
      valueLabel: label,
    }))

const getSwitchDefaultValue = options =>
  (options.find(({ checked }) => !!checked) || { value: '' }).value

const getPlaceholderForSelect = valueList =>
  (valueList.find(({ value }) => !value) || {}).label

class PaymentMethodFormField extends React.PureComponent {
  render() {
    switch (this.props.type) {
      case 'checkbox':
        return (
          <FormSwitch
            id={this.props.name}
            label=""
            text={this.props.label}
            onChange={this.props.onChange}
            checked={!!this.props.defaultValue}
            disabled={this.props.disabled}
            required={
              this.props.mandatory
                ? getText('payApply.paymentMethodForm.mandatory')
                : null
            }
            isValid={this.props.isValid}
            defaultValue={getSwitchDefaultValue(this.props.options)}
          />
        )
      case 'switch':
        return (
          <div style={{ paddingBottom: '1rem' }}>
            <Switch
              id={this.props.name}
              label=""
              text={this.props.label}
              onChange={e => this.props.onChange(e.target.checked)}
              checked={this.props.defaultValue || false}
              disabled={this.props.disabled}
            />
          </div>
        )
      case 'bold-switch':
        return (
          <div style={{ paddingBottom: '1rem' }}>
            <Switch
              id={this.props.name}
              label=""
              text={this.props.label}
              boldText
              onChange={e => this.props.onChange(e.target.checked)}
              checked={this.props.defaultValue || false}
              disabled={this.props.disabled}
            />
          </div>
        )
      case 'list':
        return (
          <div style={{ paddingBottom: '1rem' }}>
            <FormSelect
              id={this.props.name}
              key={this.props.name || this.props.label}
              label={this.props.label}
              values={getValuesForSelect(this.props.options)}
              optionsLabel={
                this.props.defaultValue
                  ? null
                  : getPlaceholderForSelect(this.props.options) ||
                    (this.props.mandatory
                      ? getText('payApply.paymentMethodForm.optionsLabel')
                      : null)
              }
              required={
                this.props.mandatory
                  ? getText('payApply.paymentMethodForm.optionsLabel')
                  : null
              }
              defaultValue={this.props.defaultValue}
              onChange={this.props.onChange}
              isValid={this.props.isValid}
            />
          </div>
        )
      case 'text':
        return (
          <div style={{ paddingBottom: '1rem' }}>
            <FormTextInput
              id={this.props.name}
              key={this.props.name || this.props.label}
              label={this.props.label}
              required={this.props.mandatory ? this.props.format.error : null}
              readonly={this.props.readonly}
              format={this.props.format}
              placeholder={this.props.description}
              value={this.props.defaultValue || ''}
              onChange={e => this.props.onChange(e.target.value)}
              isValid={this.props.isValid}
              disabled={this.props.disabled}
              autoFocus={this.props.focus()}
              additionalValidation={input =>
                this.props.additionalValidation(input)
              }
              disableAutocomplete
            />
            {this.props.children}
          </div>
        )
      case 'integer':
        return (
          <div style={{ paddingBottom: '1rem' }}>
            <FormTextInput
              id={this.props.name}
              key={this.props.name || this.props.label}
              type="number"
              label={this.props.label}
              required={this.props.mandatory ? this.props.format.error : null}
              readonly={this.props.readonly}
              format={
                this.props.format && this.props.format.regex
                  ? this.props.format
                  : format.integer
              }
              placeholder={this.props.description}
              value={this.props.defaultValue || ''}
              onChange={e => this.props.onChange(e.target.value)}
              isValid={this.props.isValid}
              disabled={this.props.disabled}
              autoFocus={this.props.focus()}
              additionalValidation={this.props.additionalValidation}
              min={this.props.min}
              max={this.props.max}
              disableAutocomplete
            />
          </div>
        )
      case 'currency':
        return (
          <div style={{ paddingBottom: '1rem' }}>
            <CurrencyInput
              placeholderRange
              showOnlyCurrencyNoRange={this.props.showOnlyCurrencyNoRange}
              id={this.props.name}
              label={this.props.label}
              wrapLabel
              disabled={this.props.disabled}
              value={this.props.defaultValue}
              isValid={this.props.isValid}
              help={this.props.helpText}
              onValueChange={value => this.props.onChange(value.floatValue)}
              required={this.props.mandatory ? this.props.format.error : null}
              boundaries={{
                min: {
                  value: this.props.min,
                  error: getText(
                    'payApply.paymentMethod.creditMustBeMoreThanLowerBoundry',
                  ),
                },
                max: {
                  value: this.props.max,
                  error: getText(
                    'payApply.paymentMethod.cretidMustBeLessThanUpperBoundry',
                  ),
                },
              }}
            />
          </div>
        )
      case 'hidden':
        return (
          <FormTextInput
            id={this.props.name}
            key={this.props.name || this.props.label}
            type="hidden"
            required={this.props.mandatory ? this.props.format.error : null}
            format={this.props.format}
            value={
              this.props.defaultValue != null
                ? `${this.props.defaultValue}`
                : ''
            }
            onChange={e => this.props.onChange(e.target.value)}
            isValid={this.props.isValid}
            disabled={this.props.disabled}
            disableAutocomplete
          />
        )
      case 'confirmation':
        return (
          <Checkbox
            id={this.props.name}
            label={this.props.label}
            text={this.props.options[0].label}
            checked={this.props.defaultValue || false}
            onChange={e => this.props.onChange(e.target.checked)}
            disabled={this.props.disabled}
          />
        )
      case 'br':
        return <br />
      case 'group-label':
        return (
          <label style={{ marginTop: '1rem' }} className={midas.formLabel}>
            {this.props.label}
          </label>
        )
      case 'step-integer':
        return (
          <div style={{ paddingBottom: '1rem' }}>
            <FormTextInput
              stepControls
              id={this.props.name}
              key={this.props.name || this.props.label}
              type="number"
              label={this.props.label}
              required={this.props.mandatory ? this.props.format.error : null}
              readonly={this.props.readonly}
              format={
                this.props.format && this.props.format.regex
                  ? this.props.format
                  : format.integer
              }
              placeholder={this.props.description}
              value={this.props.defaultValue || ''}
              onChange={e => this.props.onChange(e.target.value)}
              isValid={this.props.isValid}
              disabled={this.props.disabled}
              autoFocus={this.props.focus()}
              additionalValidation={this.props.additionalValidation}
              min={this.props.min}
              max={this.props.max}
              disableAutocomplete
            />
          </div>
        )
      default:
        return null
    }
  }
}

PaymentMethodFormField.propTypes = {
  type: PropTypes.string.isRequired,
  name: PropTypes.string,
  helpText: PropTypes.string,
  label: PropTypes.string.isRequired,
  description: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  options: PropTypes.array,
  mandatory: PropTypes.bool,
  readonly: PropTypes.bool,
  format: PropTypes.object,
  defaultValue: PropTypes.any,
  isValid: PropTypes.func,
  additionalValidation: PropTypes.func,
  disabled: PropTypes.bool,
  focus: PropTypes.func,
  children: PropTypes.any,
  max: PropTypes.number,
  min: PropTypes.number,
  showOnlyCurrencyNoRange: PropTypes.bool,
}

PaymentMethodFormField.defaultProps = {
  defaultValue: null,
  helpText: null,
  format: null,
  name: null,
  options: null,
  mandatory: false,
  readonly: false,
  isValid: null,
  disabled: false,
  focus: () => false,
  children: null,
  additionalValidation: () => {},
  max: null,
  min: null,
  showOnlyCurrencyNoRange: true,
}

export default PaymentMethodFormField
